import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  fetchInvites,
  fetchWorkspace,
  disableInvite,
  downloadReportExport,
  resendInviteEmails,
  updateInvite,
} from "../api/adminClient";
import {
  formatInviteStatus,
  inviteStatusClass,
  normalizePipelineStatus,
  participantAssessmentUrl,
  rolesFromWorkspace,
  workspaceLiteInviteAvailable,
} from "./inviteConstants";
import InviteRoleField from "./InviteRoleField";
import { LENS_ROUTES } from "./routes";
import { fetchProfiles, generateModularReport, createProfile, fetchBlockCatalog, assignProfileToEngagement, fetchAssignments } from "../api/reportProfilesClient";
import { ProfileEditorModal } from "./ReportProfilesPage";
import { fetchLiteAssessmentReportPdf } from "../api/client";
import { openReportLoadingTab } from "../utils/reportPdfLoader";
import { buildInvitesListContext } from "./inviteAssistantContext";
import {
  coerceCompanyProfileId,
  coerceTeamProfileId,
  defaultCompanyProfileIdFromList,
  defaultTeamProfileIdFromList,
  filterProfilesForWorkspace,
  formatCompanyProfileLabel,
  formatTeamProfileLabel,
} from "./reportProfileDefaults";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const PAGE_SIZE_STORAGE_KEY = "invites-page-size";
const STATUS_FILTER_OPTIONS = [
  { id: "all", label: "All" },
  { id: "unfinished", label: "Not finished" },
  { id: "Not started", label: "Not started" },
  { id: "Pending", label: "Pending" },
  { id: "in_progress", label: "In progress" },
  { id: "LiteCompleted", label: "Lite completed" },
  { id: "Completed", label: "Completed" },
];
const UNFINISHED_STATUSES = new Set(["Not started", "Pending", "LiteCompleted"]);
const IN_PROGRESS_STATUSES = new Set(["Pending", "LiteCompleted"]);
function Spinner({ className = "h-5 w-5" }) {
  return (
    <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

function IconEye({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.036 12.322a1 1 0 010-.644C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function IconMail({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
      />
    </svg>
  );
}

function IconPdf({ className = "w-4 h-4", disabled }) {
  return (
    <svg
      className={`${className} ${disabled ? "pipeline-pdf-icon--disabled" : "pipeline-pdf-icon"}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"
      />
    </svg>
  );
}

function IconDownload({ className = "w-5 h-5" }) {
  return (
    <svg className={`${className} pipeline-download-icon`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5m0 0l5-5m-5 5V4" />
    </svg>
  );
}

function IconTrash({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
      />
    </svg>
  );
}

function IconEdit({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
      />
    </svg>
  );
}

function ModalBackdrop({ children, onClose }) {
  return (
    <div
      className="admin-modal-backdrop fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-full max-h-[90vh] overflow-auto">{children}</div>
      {onClose && (
        <button
          type="button"
          className="sr-only"
          onClick={onClose}
          aria-label="Close"
        />
      )}
    </div>
  );
}

function StatCard({ label, value, accent, onClick, active }) {
  const className = `rounded-xl border bg-white px-4 py-3 shadow-sm min-w-0 text-left w-full transition-colors ${
    active
      ? "border-[#004f73]/50 ring-2 ring-[#004f73]/20"
      : "border-slate-200/80"
  } ${onClick ? "cursor-pointer hover:border-[#004f73]/35 hover:shadow-md" : ""}`;

  if (onClick) {
    return (
      <button type="button" onClick={onClick} className={className}>
        <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400 truncate">
          {label}
        </p>
        <p className={`text-2xl font-semibold tabular-nums mt-0.5 ${accent || "text-slate-900"}`}>
          {value}
        </p>
      </button>
    );
  }

  return (
    <div className={className}>
      <p className="text-[11px] font-medium uppercase tracking-wide text-slate-400 truncate">
        {label}
      </p>
      <p className={`text-2xl font-semibold tabular-nums mt-0.5 ${accent || "text-slate-900"}`}>
        {value}
      </p>
    </div>
  );
}

function getPageNumbers(current, total) {
  if (total <= 1) return [0];
  if (total <= 7) return Array.from({ length: total }, (_, i) => i);
  const pages = new Set([0, total - 1, current, current - 1, current + 1]);
  return [...pages].filter((p) => p >= 0 && p < total).sort((a, b) => a - b);
}

function escapeCsvCell(value) {
  const s = String(value ?? "");
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function exportInvitesToCsv(rows) {
  const headers = [
    "First Name",
    "Last Name",
    "Company",
    "Email",
    "Invited By",
    "Role",
    "Status",
    "Invited",
    "Assessment Link",
  ];
  const lines = rows.map((r) =>
    [
      r.first_name,
      r.last_name,
      r.company_name,
      r.email,
      r.invited_by_name || r.invited_by?.name || "",
      r.designation,
      formatInviteStatus(r.status),
      r.created_date ? new Date(r.created_date).toLocaleDateString() : "",
      participantAssessmentUrl(r.survey_token),
    ]
      .map(escapeCsvCell)
      .join(",")
  );
  const csv = [headers.join(","), ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `invited-participants-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function inviteRowId(row) {
  return row.id || row.invite_id;
}

const DOWNLOAD_MENU_W = 224;
const DOWNLOAD_MENU_H = 180;

function positionDownloadMenu(triggerEl) {
  const rect = triggerEl.getBoundingClientRect();
  const gap = 4;
  const pad = 8;
  let top = rect.bottom + gap;
  if (top + DOWNLOAD_MENU_H > window.innerHeight - pad) {
    top = rect.top - DOWNLOAD_MENU_H - gap;
  }
  let left = rect.right - DOWNLOAD_MENU_W;
  left = Math.min(Math.max(pad, left), window.innerWidth - DOWNLOAD_MENU_W - pad);
  return { top: Math.max(pad, top), left };
}

function IconRefresh({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99"
      />
    </svg>
  );
}

function IconSearch({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      <path
        fillRule="evenodd"
        d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function RowIconButton({ title, label, onClick, disabled, children, className = "" }) {
  const tip = label || title;
  return (
    <button
      type="button"
      title={tip}
      aria-label={tip}
      disabled={disabled}
      onClick={onClick}
      className={`group/btn pipeline-row-btn relative p-1.5 rounded-lg disabled:opacity-40 disabled:pointer-events-none transition-colors ${className}`}
    >
      {children}
      {tip ? (
        <span className="pipeline-row-tooltip pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-800 px-2 py-1 text-[10px] font-medium text-white opacity-0 shadow-lg transition-opacity group-hover/btn:opacity-100 z-30">
          {tip}
        </span>
      ) : null}
    </button>
  );
}

function sortRows(rows, sortKey, sortDir) {
  if (!sortKey) return rows;
  const mul = sortDir === "desc" ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = (a[sortKey] ?? "").toString().toLowerCase();
    const bv = (b[sortKey] ?? "").toString().toLowerCase();
    if (av < bv) return -1 * mul;
    if (av > bv) return 1 * mul;
    return 0;
  });
}

export default function InviteManagementPage({ embedded = false }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const outletContext = useOutletContext();
  const setAdminAssistantContext = outletContext?.setAdminAssistantContext;
  const [invites, setInvites] = useState([]);
  const [participantStats, setParticipantStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [filterStatus, setFilterStatus] = useState(() => {
    const raw = searchParams.get("status");
    return raw ? normalizePipelineStatus(raw) : "all";
  });
  const [filterCompany, setFilterCompany] = useState(() => searchParams.get("company") || "");
  const [filterRole, setFilterRole] = useState("");
  const [sortKey, setSortKey] = useState("created_date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem(PAGE_SIZE_STORAGE_KEY) || "10", 10);
      return PAGE_SIZE_OPTIONS.includes(saved) ? saved : 10;
    } catch {
      return 10;
    }
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewEmail, setViewEmail] = useState("");
  const [viewLink, setViewLink] = useState("");
  const [viewInviteId, setViewInviteId] = useState(null);

  const [resendOpen, setResendOpen] = useState(false);
  const [resendRow, setResendRow] = useState(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteEmail, setDeleteEmail] = useState("");
  const [deleteToken, setDeleteToken] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    designation: "",
  });
  const [editResend, setEditResend] = useState(false);

  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [groupCompany, setGroupCompany] = useState("");
  const [selectedGroups, setSelectedGroups] = useState([]);

  const [copiedLinkKey, setCopiedLinkKey] = useState(null);

  const [reportProfiles, setReportProfiles] = useState([]);
  const [reportCatalog, setReportCatalog] = useState([]);
  const [rowProfileMap, setRowProfileMap] = useState({});
  const [teamProfileMap, setTeamProfileMap] = useState({});
  const [showNewProfileModal, setShowNewProfileModal] = useState(false);
  const [newProfileTargetCompany, setNewProfileTargetCompany] = useState(null);
  const [newProfileReportBase, setNewProfileReportBase] = useState("company");

  const [openRowId, setOpenRowId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [liteLoadingToken, setLiteLoadingToken] = useState(null);
  const [workspace, setWorkspace] = useState(null);
  const dropdownRef = useRef(null);
  const downloadTriggerRef = useRef(null);

  const GROUPS = useMemo(() => rolesFromWorkspace(workspace), [workspace]);
  const liteAvailable = workspaceLiteInviteAvailable(workspace);
  const statusFilterOptions = STATUS_FILTER_OPTIONS.filter((opt) => {
    if (!liteAvailable && opt.id === "LiteCompleted") return false;
    if (liteAvailable && opt.id === "in_progress" && filterStatus !== "in_progress") return false;
    return true;
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWorkspace();
        if (!cancelled) setWorkspace(res.data?.workspace || null);
      } catch {
        if (!cancelled) setWorkspace(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Map designations from invite rows to a profile_id.
  // Checks all rows for a company and picks the most senior role detected.
  const autoProfileForDesignation = (rows, profiles) => {
    const ROLE_PRIORITY = [
      { keywords: ["ceo", "chief executive"], profileId: "ceo_full" },
      { keywords: ["pe", "private equity", "sponsor", "investor", "portfolio"], profileId: "pe_sponsor" },
      {
        keywords: ["coo", "cfo", "cto", "cmo", "chief", "vp", "vice president", "director", "leadership", "executive"],
        profileId: "leadership_team",
      },
    ];
    for (const { keywords, profileId } of ROLE_PRIORITY) {
      const match = rows.some((r) => {
        const d = (r.designation || "").toLowerCase();
        return keywords.some((kw) => d.includes(kw));
      });
      if (match && profiles.some((p) => p.profile_id === profileId)) return profileId;
    }
    return null;
  };

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [invRes, profRes, catRes] = await Promise.all([
        fetchInvites(),
        fetchProfiles(true).catch(() => ({ data: { profiles: [] } })),
        fetchBlockCatalog().catch(() => ({ data: { blocks: [] } })),
      ]);
      const allInvites = invRes.data?.invites || [];
      setInvites(allInvites);
      setParticipantStats(invRes.data?.stats || null);
      const profiles = (profRes.data?.profiles || []).filter(
        (p) => p.output_format !== "excel"
      );
      setReportProfiles(profiles);
      setReportCatalog(catRes.data?.blocks || []);

      // --- build company + team profile maps keyed by company_name ---
      const uniqueCompanies = [...new Set(allInvites.map((r) => r.company_name).filter(Boolean))];
      const profileMap = {};
      const teamMap = {};

      await Promise.all(
        uniqueCompanies.map(async (companyName) => {
          try {
            const res = await fetchAssignments(companyName);
            const assignments = res.data?.assignments || [];
            const saved = assignments.find((a) => !a.role);
            const teamSaved = assignments.find((a) => a.role === "team_report");
            if (saved?.profile_id) {
              profileMap[companyName] = coerceCompanyProfileId(saved.profile_id, profiles);
            }
            if (teamSaved?.profile_id) {
              teamMap[companyName] = coerceTeamProfileId(teamSaved.profile_id, profiles);
            }
          } catch {
            // ignore fetch errors — fall through to auto-detect
          }
        })
      );

      uniqueCompanies.forEach((companyName) => {
        if (profileMap[companyName]) return;
        const rows = allInvites.filter((r) => r.company_name === companyName);
        const companyOnly = filterProfilesForWorkspace(profiles).filter(
          (p) => p.is_team_base !== true
        );
        const detected = autoProfileForDesignation(rows, companyOnly);
        profileMap[companyName] = coerceCompanyProfileId(
          detected || defaultCompanyProfileIdFromList(profiles),
          profiles
        );
      });

      uniqueCompanies.forEach((companyName) => {
        if (teamMap[companyName]) return;
        teamMap[companyName] = defaultTeamProfileIdFromList(profiles);
      });

      setRowProfileMap(profileMap);
      setTeamProfileMap(teamMap);
    } catch (e) {
      toast.error(e.message || "Could not load invites");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const rawStatus = searchParams.get("status");
    const rawCompany = searchParams.get("company");
    if (rawStatus) setFilterStatus(normalizePipelineStatus(rawStatus));
    if (searchParams.has("company")) setFilterCompany(rawCompany || "");
  }, [searchParams]);

  useEffect(() => {
    if (!openRowId) return undefined;
    const onDoc = (e) => {
      const t = e.target;
      if (dropdownRef.current?.contains(t) || downloadTriggerRef.current?.contains(t)) {
        return;
      }
      setOpenRowId(null);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpenRowId(null);
    };
    const onScrollOrResize = () => {
      const el = downloadTriggerRef.current;
      if (!el) {
        setOpenRowId(null);
        return;
      }
      setDropdownPos(positionDownloadMenu(el));
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [openRowId]);

  const companyOptions = useMemo(() => {
    const names = new Set(invites.map((i) => i.company_name).filter(Boolean));
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [invites]);

  const filtered = useMemo(() => {
    let base = invites;
    if (filterStatus === "unfinished") {
      base = base.filter((row) => UNFINISHED_STATUSES.has(row.status || "Not started"));
    } else if (filterStatus === "in_progress") {
      base = base.filter((row) => IN_PROGRESS_STATUSES.has(row.status || "Not started"));
    } else if (filterStatus !== "all") {
      base = base.filter((row) => (row.status || "Not started") === filterStatus);
    }
    if (filterCompany) {
      base = base.filter((row) => row.company_name === filterCompany);
    }
    if (filterRole) {
      base = base.filter((row) => row.designation === filterRole);
    }
    const q = filterText.trim().toLowerCase();
    if (q) {
      base = base.filter((row) => {
        const haystack = [
          row.first_name,
          row.last_name,
          [row.first_name, row.last_name].filter(Boolean).join(" "),
          row.email,
          row.company_name,
          row.invited_by_name,
          row.invited_by?.name,
          row.invited_by?.email,
          row.designation,
          row.status,
          formatInviteStatus(row.status),
        ]
          .filter(Boolean)
          .map((v) => String(v).toLowerCase());
        return haystack.some((v) => v.includes(q));
      });
    }
    return sortRows(base, sortKey, sortDir);
  }, [invites, filterText, filterStatus, filterCompany, filterRole, sortKey, sortDir]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageRows = filtered.slice(page * pageSize, page * pageSize + pageSize);
  const pageNumbers = useMemo(() => getPageNumbers(page, pageCount), [page, pageCount]);

  const inviteStats = useMemo(() => {
    const companySet = new Set(invites.map((i) => i.company_name).filter(Boolean));
    const statusCounts = participantStats?.status_counts;
    if (statusCounts && typeof statusCounts === "object") {
      return {
        total: participantStats?.invite?.total_invites_sent ?? 0,
        completed: statusCounts.Completed || 0,
        notStarted: statusCounts["Not started"] || 0,
        liteCompleted: statusCounts.LiteCompleted || 0,
        companies: companySet.size,
      };
    }
    return {
      total: invites.length,
      completed: invites.filter((i) => i.status === "Completed").length,
      notStarted: invites.filter((i) => (i.status || "Not started") === "Not started").length,
      liteCompleted: invites.filter((i) => i.status === "LiteCompleted").length,
      companies: companySet.size,
    };
  }, [invites, participantStats]);

  const hasActiveFilters =
    !!filterText.trim() ||
    filterStatus !== "all" ||
    !!filterCompany ||
    !!filterRole;

  const selectedRows = useMemo(
    () => invites.filter((row) => selectedIds.includes(inviteRowId(row))),
    [invites, selectedIds]
  );

  const allPageSelected =
    pageRows.length > 0 && pageRows.every((row) => selectedIds.includes(inviteRowId(row)));

  useEffect(() => {
    if (page >= pageCount) setPage(Math.max(0, pageCount - 1));
  }, [page, pageCount]);

  useEffect(() => {
    setSelectedIds((prev) =>
      prev.filter((id) => invites.some((row) => inviteRowId(row) === id))
    );
  }, [invites]);

  useEffect(() => {
    if (!setAdminAssistantContext) return;
    setAdminAssistantContext(
      buildInvitesListContext(filtered, filterText, sortKey, sortDir, invites.length)
    );
  }, [filtered, filterText, sortKey, sortDir, invites.length, setAdminAssistantContext]);

  useEffect(() => {
    return () => setAdminAssistantContext?.(null);
  }, [setAdminAssistantContext]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const goToInvitePage = () => {
    navigate(LENS_ROUTES.engagementCompose);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await load({ silent: true });
      toast.success("List refreshed");
    } catch {
      /* load shows toast */
    } finally {
      setRefreshing(false);
    }
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(0);
    try {
      localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(size));
    } catch {
      /* ignore */
    }
  };

  const clearAllFilters = () => {
    setFilterText("");
    setFilterStatus("all");
    setFilterCompany("");
    setFilterRole("");
    setPage(0);
  };

  const applyStatusFilter = (status) => {
    setFilterStatus(status);
    setPage(0);
  };

  const toggleRowSelected = (row) => {
    const id = inviteRowId(row);
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllPage = () => {
    const pageIds = pageRows.map(inviteRowId);
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...pageIds])]);
    }
  };

  const handleBulkResend = async () => {
    if (!selectedIds.length) return;
    setBusy(true);
    try {
      await resendInviteEmails(selectedIds);
      toast.success(`Resent ${selectedIds.length} invite(s)`);
      setSelectedIds([]);
      await load();
    } catch (e) {
      toast.error(e.message || "Bulk resend failed");
    } finally {
      setBusy(false);
    }
  };

  const handleBulkExport = () => {
    if (!selectedRows.length) return;
    exportInvitesToCsv(selectedRows);
    toast.success(`Exported ${selectedRows.length} row(s)`);
  };

  const handleBulkDelete = async () => {
    const tokens = selectedRows.map((r) => r.survey_token).filter(Boolean);
    if (!tokens.length) return;
    setBusy(true);
    try {
      await Promise.all(tokens.map((t) => disableInvite(t)));
      toast.success(`Deleted ${tokens.length} invite(s)`);
      setBulkDeleteOpen(false);
      setSelectedIds([]);
      await load();
    } catch (e) {
      toast.error(e.message || "Bulk delete failed");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteToken) return;
    setBusy(true);
    try {
      await disableInvite(deleteToken);
      toast.success("Invite deleted successfully");
      setDeleteOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || "Failed to delete invite");
    } finally {
      setBusy(false);
    }
  };

  const openEditModal = (row) => {
    setEditRow(row);
    setEditForm({
      first_name: row.first_name || "",
      last_name: row.last_name || "",
      email: row.email || "",
      designation: row.designation || GROUPS[0],
    });
    setEditResend(false);
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    if (!editRow) return;
    const first_name = editForm.first_name.trim();
    const last_name = editForm.last_name.trim();
    const email = editForm.email.trim();
    const designation = editForm.designation.trim();
    if (!first_name || !email) {
      toast.error("First name and email are required");
      return;
    }
    if (!designation) {
      toast.error("Role is required");
      return;
    }
    const inviteId = inviteRowId(editRow);
    if (!inviteId) {
      toast.error("Missing invite id");
      return;
    }
    setBusy(true);
    try {
      const res = await updateInvite({
        invite_id: inviteId,
        first_name,
        last_name,
        email,
        designation,
      });
      const emailChanged = res.data?.email_changed;
      const updated = res.data || {};
      setInvites((prev) =>
        prev.map((row) =>
          inviteRowId(row) === inviteId
            ? {
                ...row,
                first_name: updated.first_name ?? first_name,
                last_name: updated.last_name ?? last_name,
                email: updated.email ?? email,
                designation: updated.designation ?? designation,
              }
            : row
        )
      );
      if (editResend && emailChanged) {
        await resendInviteEmails([inviteId]);
        toast.success("Participant updated and invite resent");
      } else {
        toast.success("Participant updated");
      }
      setEditOpen(false);
      setEditRow(null);
    } catch (e) {
      toast.error(e.message || "Failed to update participant");
    } finally {
      setBusy(false);
    }
  };

  const handleResendConfirm = async () => {
    const id = resendRow ? inviteRowId(resendRow) : null;
    if (!id) return;
    setBusy(true);
    try {
      await resendInviteEmails([id]);
      toast.success("Invitation email sent");
      setResendOpen(false);
      setResendRow(null);
      await load();
    } catch (e) {
      toast.error(e.message || "Failed to resend invite");
    } finally {
      setBusy(false);
    }
  };

  const handleSendFromView = async () => {
    if (!viewInviteId) return;
    setBusy(true);
    try {
      await resendInviteEmails([viewInviteId]);
      toast.success("Invitation email sent");
      setViewOpen(false);
      await load();
    } catch (e) {
      toast.error(e.message || "Failed to send email");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLinkKey(key);
      window.setTimeout(() => setCopiedLinkKey(null), 2000);
    } catch {
      toast.error("Failed to copy link");
    }
  };

  const downloadRowReport = async (row, type) => {
    let path = "";
    if (type === "individual") {
      path = `export_survey_responses?company_name=${encodeURIComponent(row.company_name)}&user_id=${encodeURIComponent(row.user_id || "")}`;
    } else if (type === "company") {
      path = `export_survey_responses?company_name=${encodeURIComponent(row.company_name)}`;
    } else return;

    const p = downloadReportExport(path);
    toast.promise(p, {
      loading: "Downloading report…",
      success: "Report downloaded successfully!",
      error: (e) => e.message || "Failed to download report",
    });
    await p.catch(() => {});
  };

  const downloadGroupReport = async () => {
    if (!selectedGroups.length) {
      toast.error("Please select at least one group.");
      return;
    }
    const designation = encodeURIComponent(selectedGroups.join(","));
    const path = `export_survey_responses?company_name=${encodeURIComponent(groupCompany)}&designation=${designation}`;
    const p = downloadReportExport(path);
    toast.promise(p, {
      loading: "Downloading file…",
      success: "File downloaded successfully!",
      error: (e) => e.message || "Failed to download the file",
    });
    try {
      await p;
      setGroupModalOpen(false);
      setSelectedGroups([]);
      setGroupCompany("");
    } catch {
      /* toast handled */
    }
  };

  const companyProfiles = useMemo(
    () =>
      filterProfilesForWorkspace(reportProfiles).filter(
        (p) => p.is_team_base !== true
      ),
    [reportProfiles]
  );
  const teamProfiles = useMemo(
    () =>
      filterProfilesForWorkspace(reportProfiles).filter(
        (p) => p.is_team_base === true
      ),
    [reportProfiles]
  );
  const defaultCompanyProfileId = useMemo(
    () => defaultCompanyProfileIdFromList(reportProfiles),
    [reportProfiles]
  );
  const defaultTeamProfileId = useMemo(
    () => defaultTeamProfileIdFromList(reportProfiles),
    [reportProfiles]
  );

  const openTeamPdf = (companyName, profileId) => {
    const resolvedId = coerceTeamProfileId(
      profileId || defaultTeamProfileId,
      reportProfiles
    );
    const win = openReportLoadingTab("Generating team report…");
    const p = generateModularReport(companyName, resolvedId, { format: "pdf" })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        if (win) {
          win.location.href = url;
        } else {
          const a = document.createElement("a");
          a.href = url;
          a.download = `${companyName.replace(/\s+/g, "_")}_Team_Report.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((err) => {
        if (win) win.close();
        throw err;
      });
    toast.promise(p, {
      loading: "Generating team report…",
      success: "Team report ready",
      error: (e) => e?.message || "Could not generate team report",
    });
  };

  const openLiteReport = (surveyToken) => {
    if (!surveyToken) {
      toast.error("No assessment link for this participant");
      return;
    }
    if (liteLoadingToken === surveyToken) return;

    // Open a tab immediately so users get instant click feedback and no popup blocking.
    const win = openReportLoadingTab("Generating lite report…");

    setLiteLoadingToken(surveyToken);
    const p = fetchLiteAssessmentReportPdf(surveyToken)
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        if (win) {
          win.location.href = url;
        } else {
          window.open(url, "_blank", "noopener,noreferrer");
        }
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((err) => {
        if (win) win.close();
        throw err;
      })
      .finally(() => {
        setLiteLoadingToken((prev) => (prev === surveyToken ? null : prev));
      });

    toast.promise(p, {
      loading: "Generating lite report…",
      success: "Lite report opened",
      error: (e) => e?.message || "Could not open lite report",
    });
  };

  const openCompanyPdf = (companyName, profileId) => {
    const resolvedId = coerceCompanyProfileId(
      profileId || defaultCompanyProfileId,
      reportProfiles
    );
    // Open a blank tab synchronously (within the click handler) so the
    // browser doesn't treat it as a popup. Then navigate it to the PDF blob.
    const win = openReportLoadingTab(
      resolvedId === "company_analyzed" ? "Generating analyzed company report…" : "Generating company report…"
    );
    const p = generateModularReport(companyName, resolvedId, { format: "pdf" })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        if (win) {
          win.location.href = url;
        } else {
          // Fallback: download if popup was blocked
          const a = document.createElement("a");
          a.href = url;
          a.download = `${companyName.replace(/\s+/g, "_")}_Company_Report.pdf`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((err) => {
        if (win) win.close();
        throw err;
      });
    toast.promise(p, {
      loading: "Generating company report…",
      success: "Company report ready",
      error: (e) => e?.message || "Could not generate company report",
    });
  };

  const reportSelectClass =
    "admin-filter-select text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 max-w-[140px]";

  const SortTh = ({ label, field }) => {
    const active = sortKey === field;
    return (
      <th
        className="px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 cursor-pointer select-none whitespace-nowrap hover:text-slate-800 transition-colors border-b border-slate-200 bg-slate-50/95"
        onClick={() => toggleSort(field)}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          <span className={`text-slate-400 ${active ? "text-[#004f73]" : "opacity-40"}`}>
            {active ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
          </span>
        </span>
      </th>
    );
  };

  const GroupTh = ({ label, colSpan, className = "", variant = "actions" }) => {
    const variantClass =
      variant === "participant"
        ? "bg-gradient-to-r from-white via-slate-50/90 to-[#004f73]/[0.04] text-slate-500 border-b border-slate-200/70"
        : variant === "reports"
          ? "bg-gradient-to-r from-white via-slate-50/90 to-amber-50/35 text-slate-500 border-b border-slate-200/70"
          : "bg-slate-50/90 text-slate-400 border-b border-slate-200/80";
    return (
      <th
        colSpan={colSpan}
        className={`px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider ${variantClass} ${className}`}
      >
        {label}
      </th>
    );
  };

  return (
    <div className={embedded ? "nexus-surface-embed" : "min-h-full w-full bg-white"}>
      <div className={embedded ? "w-full" : "w-full px-6 py-6"}>
        {!embedded ? (
        <header className="mb-6">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-[#004f73]/70 mb-2">
            Admin
          </p>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Invited Participants</h1>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-50"
                onClick={handleRefresh}
                disabled={refreshing || loading}
                title="Refresh list"
              >
                <IconRefresh className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
                Refresh
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-lg transition-colors"
                onClick={goToInvitePage}
              >
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden>
                  <path
                    fillRule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
                Send invite
              </button>
            </div>
          </div>
        </header>
        ) : (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors disabled:opacity-50"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            title="Refresh list"
          >
            <IconRefresh className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-lg transition-colors"
            onClick={goToInvitePage}
          >
            Send invite
          </button>
        </div>
        )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Spinner className="h-8 w-8 text-[#004f73]" />
          <p className="text-sm text-slate-400">Loading participants…</p>
        </div>
      ) : invites.length === 0 ? (
        <div className="text-center py-20 px-6 rounded-2xl border border-dashed border-slate-200 bg-slate-50/50">
          <div className="mx-auto w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center mb-4">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              className="w-6 h-6 text-slate-400"
              aria-hidden
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"
              />
            </svg>
          </div>
          <p className="text-base font-medium text-slate-700">No invites yet</p>
          <p className="text-sm text-slate-500 mt-1">
            Send your first invite to start collecting assessments.
          </p>
          <button
            type="button"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-[#004f73] hover:bg-[#003a57] rounded-xl shadow-sm transition-colors"
            onClick={goToInvitePage}
          >
            Send invite
          </button>
        </div>
      ) : (
        <>
          <div className={`grid grid-cols-2 sm:grid-cols-3 ${liteAvailable ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-3 mb-6`}>
            <StatCard
              label="Participants"
              value={inviteStats.total}
              onClick={() => applyStatusFilter("all")}
              active={filterStatus === "all" && !filterCompany && !filterRole}
            />
            <StatCard
              label="Completed"
              value={inviteStats.completed}
              accent="text-emerald-600"
              onClick={() => applyStatusFilter("Completed")}
              active={filterStatus === "Completed"}
            />
            <StatCard
              label="Not started"
              value={inviteStats.notStarted}
              accent="text-slate-600"
              onClick={() => applyStatusFilter("Not started")}
              active={filterStatus === "Not started"}
            />
            {liteAvailable ? (
              <StatCard
                label="Lite completed"
                value={inviteStats.liteCompleted}
                accent="text-amber-600"
                onClick={() => applyStatusFilter("LiteCompleted")}
                active={filterStatus === "LiteCompleted"}
              />
            ) : null}
            <StatCard label="Companies" value={inviteStats.companies} accent="text-[#004f73]" />
          </div>

          <div className="flex flex-col gap-3 mb-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2 min-w-0">
                <p className="text-sm text-slate-500 shrink-0">
                  {hasActiveFilters ? (
                    <>
                      Showing <span className="font-medium text-slate-700">{filtered.length}</span> of{" "}
                      {invites.length}
                    </>
                  ) : (
                    <>
                      <span className="font-medium text-slate-700">{filtered.length}</span> participant
                      {filtered.length === 1 ? "" : "s"}
                    </>
                  )}
                </p>
                <select
                  value={filterStatus}
                  onChange={(e) => applyStatusFilter(e.target.value)}
                  className="admin-filter-select text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 min-w-[130px]"
                >
                  {statusFilterOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.id === "all" ? "All statuses" : opt.label}
                    </option>
                  ))}
                </select>
                <select
                  value={filterCompany}
                  onChange={(e) => {
                    setFilterCompany(e.target.value);
                    setPage(0);
                  }}
                  className="admin-filter-select text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 min-w-[140px]"
                >
                  <option value="">All companies</option>
                  {companyOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <select
                  value={filterRole}
                  onChange={(e) => {
                    setFilterRole(e.target.value);
                    setPage(0);
                  }}
                  className="admin-filter-select text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 min-w-[120px]"
                >
                  <option value="">All roles</option>
                  {GROUPS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                {hasActiveFilters && (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="text-xs font-medium text-[#004f73] hover:underline px-1 shrink-0"
                  >
                    Clear filters
                  </button>
                )}
              </div>
              <div className="relative w-full lg:w-72 shrink-0">
                <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="text"
                  placeholder="Search name, email, company, invited by, role, status…"
                  value={filterText}
                  onChange={(e) => {
                    setFilterText(e.target.value);
                    setPage(0);
                  }}
                  className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#004f73]/20 focus:border-[#004f73]/30"
                />
              </div>
            </div>

            {selectedIds.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-xl bg-[#004f73]/5 border border-[#004f73]/15">
                <span className="text-sm font-medium text-[#004f73]">
                  {selectedIds.length} selected
                </span>
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                  disabled={busy}
                  onClick={handleBulkResend}
                >
                  Resend invites
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                  onClick={handleBulkExport}
                >
                  Export selected
                </button>
                <button
                  type="button"
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                  disabled={busy}
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  Delete selected
                </button>
                <button
                  type="button"
                  className="px-2 py-1.5 text-xs text-slate-500 hover:text-slate-700 ml-auto"
                  onClick={() => setSelectedIds([])}
                >
                  Clear selection
                </button>
              </div>
            )}
          </div>

          <div className="admin-data-table rounded-xl border border-slate-200/80 shadow-sm overflow-hidden bg-white">
            <div className="admin-table-scroll-wrap overflow-x-auto">
            <table className="w-full text-sm min-w-[1200px]">
              <thead className="sticky top-0 z-10">
                <tr>
                  <GroupTh label="Participant" colSpan={9} variant="participant" />
                  <GroupTh label="Reports" colSpan={3} variant="reports" className="text-center" />
                  <GroupTh label="Actions" colSpan={1} variant="actions" className="w-36 text-center" />
                </tr>
                <tr>
                  <th className="px-3 py-2.5 w-10 border-b border-slate-200 bg-slate-50/95">
                    <input
                      type="checkbox"
                      checked={allPageSelected}
                      onChange={toggleSelectAllPage}
                      disabled={pageRows.length === 0}
                      className="w-4 h-4 rounded border-slate-300 text-[#004f73] focus:ring-[#004f73]/30"
                      aria-label="Select all on page"
                    />
                  </th>
                  <SortTh label="First" field="first_name" />
                  <SortTh label="Last" field="last_name" />
                  <SortTh label="Company" field="company_name" />
                  <SortTh label="Email" field="email" />
                  <SortTh label="Invited by" field="invited_by_name" />
                  <SortTh label="Invited" field="created_date" />
                  <SortTh label="Role" field="designation" />
                  <SortTh label="Status" field="status" />
                  <th className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap border-b border-slate-200 bg-slate-50/95">
                    Lite
                  </th>
                  <th className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[180px] border-b border-slate-200 bg-slate-50/95">
                    Team
                  </th>
                  <th className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 min-w-[180px] border-b border-slate-200 bg-slate-50/95">
                    Company
                  </th>
                  <th className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 bg-slate-50/95">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageRows.length === 0 && invites.length > 0 ? (
                  <tr>
                    <td colSpan={13} className="px-6 py-16 text-center">
                      <p className="text-base font-medium text-slate-700">No matches</p>
                      <p className="text-sm text-slate-500 mt-1">
                        Try adjusting your search or filters.
                      </p>
                      {hasActiveFilters && (
                        <button
                          type="button"
                          onClick={clearAllFilters}
                          className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#004f73] bg-[#004f73]/5 hover:bg-[#004f73]/10 rounded-lg transition-colors"
                        >
                          Clear filters
                        </button>
                      )}
                    </td>
                  </tr>
                ) : null}
                {pageRows.map((row) => {
                  const rowId = inviteRowId(row);
                  const isSelected = selectedIds.includes(rowId);
                  const completed = row.status === "Completed";
                  const liteReady = Boolean(row.lite_report_available);
                  const displayStatus = formatInviteStatus(row.status);
                  const liteLoading = liteLoadingToken === row.survey_token;
                  return (
                    <tr
                      key={rowId}
                      className={`group hover:bg-slate-50/80 transition-colors ${isSelected ? "bg-[#004f73]/[0.03]" : ""}`}
                    >
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRowSelected(row)}
                          className="w-4 h-4 rounded border-slate-300 text-[#004f73] focus:ring-[#004f73]/30"
                          aria-label={`Select ${row.first_name} ${row.last_name}`}
                        />
                      </td>
                      <td
                        className="px-3 py-3 truncate max-w-[110px] font-medium text-slate-800"
                        title={row.first_name}
                      >
                        {row.first_name || "—"}
                      </td>
                      <td className="px-3 py-3 truncate max-w-[110px] text-slate-700" title={row.last_name}>
                        {row.last_name || "—"}
                      </td>
                      <td
                        className="px-3 py-3 truncate max-w-[120px] text-slate-600"
                        title={row.company_name}
                      >
                        {row.company_name || "—"}
                      </td>
                      <td className="px-3 py-3 truncate max-w-[160px] text-[#004f73]" title={row.email}>
                        {row.email || "—"}
                      </td>
                      <td
                        className="px-3 py-3 truncate max-w-[140px] text-slate-700"
                        title={row.invited_by_name || row.invited_by?.name || ""}
                      >
                        {row.invited_by_name || row.invited_by?.name || "—"}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-slate-600 text-xs tabular-nums">
                        {row.created_date
                          ? new Date(row.created_date).toLocaleDateString()
                          : "—"}
                      </td>
                      <td className="px-3 py-3 truncate max-w-[130px] text-slate-600 text-xs" title={row.designation}>
                        {row.designation || "—"}
                      </td>
                      <td className="px-3 py-3">
                        <span
                          className={inviteStatusClass(row.status)}
                          title={row.status === "LiteCompleted" ? "LiteCompleted" : row.status}
                        >
                          {displayStatus}
                        </span>
                      </td>
                      <td className="px-3 py-3">
                        <div
                          className={`flex items-center justify-center ${
                            !liteReady ? "opacity-40 pointer-events-none" : ""
                          }`}
                        >
                          <RowIconButton
                            label={liteLoading ? "Generating…" : "Lite report"}
                            disabled={!liteReady || !row.survey_token || liteLoading}
                            onClick={() => openLiteReport(row.survey_token)}
                          >
                            {liteLoading ? (
                              <Spinner className="h-4 w-4 text-[#004f73]" />
                            ) : (
                              <IconPdf disabled={!liteReady} />
                            )}
                          </RowIconButton>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className={`flex items-center gap-1.5 ${!completed ? "opacity-40 pointer-events-none" : ""}`}>
                          <select
                            value={coerceTeamProfileId(
                              teamProfileMap[row.company_name],
                              reportProfiles
                            )}
                            onChange={async (e) => {
                              const val = e.target.value;
                              if (val === "__new__") {
                                setNewProfileTargetCompany(row.company_name);
                                setNewProfileReportBase("team");
                                setShowNewProfileModal(true);
                              } else {
                                setTeamProfileMap((prev) => ({ ...prev, [row.company_name]: val }));
                                try {
                                  await assignProfileToEngagement(row.company_name, "team_report", val);
                                } catch {
                                  toast.error("Could not save team report profile assignment.");
                                }
                              }
                            }}
                            className={reportSelectClass}
                          >
                            {teamProfiles.length > 0
                              ? teamProfiles.map((p) => (
                                  <option key={p.profile_id} value={p.profile_id}>
                                    {formatTeamProfileLabel(p)}
                                  </option>
                                ))
                              : <option value={defaultTeamProfileId}>Team Analyzed Report</option>}
                            <option disabled>──────────────</option>
                            <option value="__new__">+ New Custom Profile</option>
                          </select>
                          <RowIconButton
                            label="Team report"
                            onClick={() =>
                              openTeamPdf(
                                row.company_name,
                                teamProfileMap[row.company_name] || defaultTeamProfileId
                              )
                            }
                          >
                            <IconPdf />
                          </RowIconButton>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className={`flex items-center gap-1.5 ${!completed ? "opacity-40 pointer-events-none" : ""}`}>
                          <select
                            value={coerceCompanyProfileId(
                              rowProfileMap[row.company_name],
                              reportProfiles
                            )}
                            onChange={async (e) => {
                              const val = e.target.value;
                              if (val === "__new__") {
                                setNewProfileTargetCompany(row.company_name);
                                setNewProfileReportBase("company");
                                setShowNewProfileModal(true);
                              } else {
                                setRowProfileMap((prev) => ({ ...prev, [row.company_name]: val }));
                                try {
                                  await assignProfileToEngagement(row.company_name, null, val);
                                } catch {
                                  toast.error("Could not save company report profile assignment.");
                                }
                              }
                            }}
                            className={reportSelectClass}
                          >
                            {companyProfiles.length > 0
                              ? companyProfiles.map((p) => (
                                  <option key={p.profile_id} value={p.profile_id}>
                                    {formatCompanyProfileLabel(p)}
                                  </option>
                                ))
                              : (
                                <option value={defaultCompanyProfileId}>
                                  Company Analyzed Report (AI Professional)
                                </option>
                              )}
                            <option disabled>──────────────</option>
                            <option value="__new__">+ New Custom Profile</option>
                          </select>
                          <RowIconButton
                            label="Company report"
                            onClick={() =>
                              openCompanyPdf(
                                row.company_name,
                                rowProfileMap[row.company_name] || defaultCompanyProfileId
                              )
                            }
                          >
                            <IconPdf />
                          </RowIconButton>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-0.5 relative rounded-lg bg-slate-50 p-0.5 w-fit">
                          <RowIconButton
                            label="View"
                            onClick={() => {
                              setViewInviteId(rowId);
                              setViewEmail(row.email);
                              setViewLink(
                                participantAssessmentUrl(row.survey_token)
                              );
                              setViewOpen(true);
                            }}
                          >
                            <IconEye />
                          </RowIconButton>
                          <RowIconButton
                            label="Edit"
                            onClick={() => openEditModal(row)}
                          >
                            <IconEdit />
                          </RowIconButton>
                          <RowIconButton
                            label="Resend"
                            onClick={() => {
                              setResendRow(row);
                              setResendOpen(true);
                            }}
                          >
                            <IconMail />
                          </RowIconButton>
                          <span
                            ref={openRowId === row.id ? downloadTriggerRef : null}
                            className="inline-flex"
                          >
                            <RowIconButton
                              disabled={!completed}
                              label="Download"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!completed) return;
                                if (openRowId === row.id) {
                                  setOpenRowId(null);
                                  return;
                                }
                                setDropdownPos(positionDownloadMenu(e.currentTarget));
                                setOpenRowId(row.id);
                              }}
                            >
                              <IconDownload />
                            </RowIconButton>
                          </span>
                          <RowIconButton
                            label="Delete"
                            className="hover:text-red-600"
                            onClick={() => {
                              setDeleteEmail(row.email);
                              setDeleteToken(row.survey_token);
                              setDeleteOpen(true);
                            }}
                          >
                            <IconTrash />
                          </RowIconButton>

                          {completed && openRowId === row.id
                            ? createPortal(
                                <div
                                  ref={dropdownRef}
                                  className="fixed w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-[9999] py-1 overflow-hidden"
                                  style={{ top: dropdownPos.top, left: dropdownPos.left }}
                                >
                                  <p className="px-4 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100">
                                    Download
                                  </p>
                                  <button
                                    type="button"
                                    className="w-full flex items-center px-4 py-2.5 hover:bg-slate-50 text-sm text-slate-700"
                                    onClick={() => {
                                      downloadRowReport(row, "individual");
                                      setOpenRowId(null);
                                    }}
                                  >
                                    Individual Data
                                  </button>
                                  <button
                                    type="button"
                                    className="w-full flex items-center px-4 py-2.5 hover:bg-slate-50 text-sm text-slate-700"
                                    onClick={() => {
                                      downloadRowReport(row, "company");
                                      setOpenRowId(null);
                                    }}
                                  >
                                    Company Data
                                  </button>
                                  <button
                                    type="button"
                                    className="w-full flex items-center px-4 py-2.5 hover:bg-slate-50 text-sm text-slate-700"
                                    onClick={() => {
                                      setGroupCompany(row.company_name);
                                      setGroupModalOpen(true);
                                      setOpenRowId(null);
                                    }}
                                  >
                                    Group Data (Leadership)
                                  </button>
                                </div>,
                                document.body
                              )
                            : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>

          {filtered.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-slate-50/50 text-sm text-slate-600">
              <div className="flex flex-wrap items-center gap-3">
                <span className="tabular-nums text-xs">
                  {page * pageSize + 1}–{Math.min((page + 1) * pageSize, filtered.length)} of{" "}
                  {filtered.length}
                </span>
                <label className="flex items-center gap-2 text-xs">
                  <span className="text-slate-500">Rows per page</span>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="border border-slate-200 rounded-lg px-2 py-1 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#004f73]/20"
                  >
                    {PAGE_SIZE_OPTIONS.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              {pageCount > 1 && (
                <div className="flex flex-wrap items-center gap-1">
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Previous
                  </button>
                  {pageNumbers.map((p, i) => {
                    const prev = pageNumbers[i - 1];
                    const showEllipsis = i > 0 && p - prev > 1;
                    return (
                      <Fragment key={p}>
                        {showEllipsis ? (
                          <span className="px-1 text-xs text-slate-400">…</span>
                        ) : null}
                        <button
                          type="button"
                          className={`min-w-[2rem] px-2 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                            page === p
                              ? "border-[#004f73] bg-[#004f73] text-white"
                              : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                          }`}
                          onClick={() => setPage(p)}
                        >
                          {p + 1}
                        </button>
                      </Fragment>
                    );
                  })}
                  <button
                    type="button"
                    className="px-3 py-1.5 text-xs font-medium rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:pointer-events-none transition-colors"
                    disabled={page >= pageCount - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
          </div>
        </>
      )}
      </div>

      {viewOpen && (
        <ModalBackdrop onClose={() => setViewOpen(false)}>
          <div className="admin-modal-card rounded-lg shadow max-w-2xl mx-auto p-6">
            <button
              type="button"
              className="float-right admin-modal-muted hover:text-slate-300"
              onClick={() => setViewOpen(false)}
            >
              ×
            </button>
            <p className="admin-modal-title text-2xl text-center font-semibold mb-3">
              Send invitation
            </p>
            <p className="text-center admin-modal-body mb-4 text-sm">
              Review the recipient and send the invite email, or copy the assessment link.
            </p>
            <div className="admin-modal-inset flex items-center justify-between p-3 border rounded-lg shadow-sm mb-4 gap-2">
              <span className="text-sm truncate admin-modal-body">{viewEmail}</span>
              <button type="button" className="blue_btn text-sm shrink-0" disabled={busy} onClick={handleSendFromView}>
                {busy ? "Sending…" : "Send email"}
              </button>
            </div>
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t admin-modal-divider" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="admin-modal-or px-2 admin-modal-muted">OR</span>
              </div>
            </div>
            <button
              type="button"
              className="white_btn w-full py-2"
              onClick={() => copyLink(viewLink, "view")}
            >
              {copiedLinkKey === "view" ? "Copied" : "Copy Link"}
            </button>
          </div>
        </ModalBackdrop>
      )}

      {resendOpen && resendRow && (
        <ModalBackdrop>
          <div className="admin-modal-card rounded-lg shadow max-w-xl mx-auto p-6 text-center">
            <p className="admin-modal-title text-2xl font-semibold mb-3">Resend Reminder</p>
            <p className="admin-modal-body text-sm mb-6">
              Send a quick, friendly reminder to{" "}
              <strong>
                {resendRow.first_name} {resendRow.last_name}
              </strong>{" "}
              ({resendRow.email})? The email will open with &ldquo;As a reminder&hellip;&rdquo; and use the
              reminder template for their role.
            </p>
            <div className="flex justify-center gap-4">
              <button type="button" className="grey_btn text-sm" onClick={() => setResendOpen(false)}>
                No
              </button>
              <button type="button" className="blue_btn text-sm" disabled={busy} onClick={handleResendConfirm}>
                {busy ? "Sending…" : "Yes"}
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {deleteOpen && (
        <ModalBackdrop>
          <div className="admin-modal-card rounded-lg shadow max-w-md mx-auto">
            <div className="p-4 border-b admin-modal-divider flex justify-between items-center">
              <h3 className="font-semibold admin-modal-title">Delete Invite</h3>
              <button type="button" onClick={() => setDeleteOpen(false)}>
                ×
              </button>
            </div>
            <div className="p-4">
              <p className="admin-modal-body mb-6">
                Are you sure you want to delete the invite for{" "}
                <span className="font-semibold">{deleteEmail}</span>? This cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button type="button" className="grey_btn text-sm" onClick={() => setDeleteOpen(false)}>
                  Cancel
                </button>
                <button type="button" className="blue_btn text-sm" disabled={busy} onClick={handleDelete}>
                  {busy ? "Deleting…" : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {editOpen && editRow && (
        <ModalBackdrop onClose={() => !busy && setEditOpen(false)}>
          <div className="admin-modal-card rounded-lg shadow max-w-lg mx-auto">
            <div className="p-4 border-b admin-modal-divider flex justify-between items-center">
              <h3 className="font-semibold admin-modal-title">Edit Participant</h3>
              <button
                type="button"
                onClick={() => !busy && setEditOpen(false)}
                className="admin-modal-muted hover:text-slate-300 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-3">
              <p className="text-sm admin-modal-muted">
                Updates name, email, and role. The assessment link stays the same.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium admin-modal-body mb-1">
                    First name
                  </label>
                  <input
                    type="text"
                    value={editForm.first_name}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, first_name: e.target.value }))
                    }
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004f73]/25"
                    disabled={busy}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium admin-modal-body mb-1">
                    Last name
                  </label>
                  <input
                    type="text"
                    value={editForm.last_name}
                    onChange={(e) =>
                      setEditForm((prev) => ({ ...prev, last_name: e.target.value }))
                    }
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004f73]/25"
                    disabled={busy}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium admin-modal-body mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004f73]/25"
                  disabled={busy}
                />
              </div>
              <div>
                <label className="block text-sm font-medium admin-modal-body mb-1">
                  Role
                </label>
                <InviteRoleField
                  roleOptions={GROUPS}
                  value={editForm.designation}
                  onChange={(designation) =>
                    setEditForm((prev) => ({ ...prev, designation }))
                  }
                  disabled={busy}
                  selectClassName="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#004f73]/25 bg-white"
                />
              </div>
              {editForm.email.trim().toLowerCase() !==
                String(editRow.email || "").trim().toLowerCase() && (
                <label className="admin-modal-check flex items-start gap-2 p-3 border rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editResend}
                    onChange={(e) => setEditResend(e.target.checked)}
                    className="mt-0.5 w-4 h-4"
                    disabled={busy}
                  />
                  <span className="text-sm admin-modal-body">
                    Resend invite email to the new address after saving
                  </span>
                </label>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  className="grey_btn text-sm"
                  onClick={() => setEditOpen(false)}
                  disabled={busy}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="blue_btn text-sm"
                  disabled={busy}
                  onClick={handleEditSave}
                >
                  {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {groupModalOpen && (
        <ModalBackdrop>
          <div className="admin-modal-card rounded-lg shadow max-w-lg mx-auto p-5">
            <h2 className="text-center font-semibold text-xl mb-1 admin-modal-title">Select Group</h2>
            <p className="text-sm admin-modal-muted text-center mb-4">
              Choose one or more groups to download their data.
            </p>
            <div className="space-y-2">
              {GROUPS.map((g) => (
                <label key={g} className="admin-modal-check flex items-center p-3 border rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedGroups.includes(g)}
                    onChange={() =>
                      setSelectedGroups((prev) =>
                        prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]
                      )
                    }
                    className="w-4 h-4"
                  />
                  <span className="ml-3">{g}</span>
                </label>
              ))}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className="grey_btn text-sm" onClick={() => setGroupModalOpen(false)}>
                Cancel
              </button>
              <button type="button" className="blue_btn text-sm" onClick={downloadGroupReport}>
                Download
              </button>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {bulkDeleteOpen && (
        <ModalBackdrop>
          <div className="admin-modal-card rounded-lg shadow max-w-md mx-auto">
            <div className="p-4 border-b admin-modal-divider flex justify-between items-center">
              <h3 className="font-semibold admin-modal-title">Delete selected invites</h3>
              <button type="button" onClick={() => setBulkDeleteOpen(false)}>
                ×
              </button>
            </div>
            <div className="p-4">
              <p className="admin-modal-body mb-6">
                Are you sure you want to delete{" "}
                <span className="font-semibold">{selectedIds.length}</span> invite
                {selectedIds.length === 1 ? "" : "s"}? This cannot be undone.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  className="grey_btn text-sm"
                  onClick={() => setBulkDeleteOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="blue_btn text-sm"
                  disabled={busy}
                  onClick={handleBulkDelete}
                >
                  {busy ? "Deleting…" : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </ModalBackdrop>
      )}

      {showNewProfileModal && (
        <ProfileEditorModal
          profile={null}
          catalog={reportCatalog}
          reportBase={newProfileReportBase}
          onSave={async (savedProfile) => {
            setShowNewProfileModal(false);
            try {
              const res = await fetchProfiles(true);
              const profiles = (res.data?.profiles || []).filter((p) => p.output_format !== "excel");
              setReportProfiles(profiles);
              if (savedProfile?.profile_id && newProfileTargetCompany) {
                if (newProfileReportBase === "team") {
                  setTeamProfileMap((prev) => ({ ...prev, [newProfileTargetCompany]: savedProfile.profile_id }));
                  try {
                    await assignProfileToEngagement(newProfileTargetCompany, "team_report", savedProfile.profile_id);
                  } catch {
                    toast.error("Could not save team report profile assignment.");
                  }
                } else {
                  setRowProfileMap((prev) => ({ ...prev, [newProfileTargetCompany]: savedProfile.profile_id }));
                  try {
                    await assignProfileToEngagement(newProfileTargetCompany, null, savedProfile.profile_id);
                  } catch {
                    toast.error("Could not save company report profile assignment.");
                  }
                }
              }
            } catch { /* silent */ }
            setNewProfileTargetCompany(null);
            setNewProfileReportBase("company");
          }}
          onClose={() => {
            setShowNewProfileModal(false);
            setNewProfileTargetCompany(null);
            setNewProfileReportBase("company");
          }}
        />
      )}
    </div>
  );
}
